import _ from "lodash";
import { DevelopmentHistoryDTO } from "../domain/models/dto/DevelopmentHistoryDTO";
import {
    IDevelopmentHistory,
    DevelopmentHistoryFiltersValues,
    DevelopmentHistoryFlatCollection,
} from "../domain/models/DevelopmentHistory";
import { DevelopmentHistoryService } from "../domain/services/DevelopmentHistoryService";
import { DevelopmentHistoryMapper } from "../mappers/DevelopmentHistoryMapper";
import { CollectionResponse, RemoveResponse } from "../types/ApiResults";
import { BaseServiceImpl } from "./BaseServiceImpl";

export class DevelopmentHistoryServiceImpl extends BaseServiceImpl implements DevelopmentHistoryService {
    static _instance: DevelopmentHistoryService;
    private developmentHistoryMapper = new DevelopmentHistoryMapper();
    private readonly developmentHistoryEndpoint = "/api/v1/development-history";

    private constructor() {
        super();
    }

    static getInstance(): DevelopmentHistoryService {
        if (!DevelopmentHistoryServiceImpl._instance) {
            DevelopmentHistoryServiceImpl._instance = new DevelopmentHistoryServiceImpl();
        }
        return DevelopmentHistoryServiceImpl._instance;
    }

    instantiate(entity: IDevelopmentHistory): IDevelopmentHistory {
        return this.developmentHistoryMapper.fromDTO(entity);
    }

    async getAll(
        currentPage: number,
        currentLimit: number,
        filtersDTO: DevelopmentHistoryFiltersValues = {} as DevelopmentHistoryFiltersValues
    ): Promise<CollectionResponse<IDevelopmentHistory>> {
        try {
            let url = `${this.developmentHistoryEndpoint}?limit=${currentLimit}&page=${currentPage}&getEntityTranslations=true`;
            Object.entries(filtersDTO).forEach(([key, value]) => {
                if (key && !_.isNil(value) && !_.isEmpty(value)) {
                    url += `&${key}=${value}`;
                }
            });

            const result = await this.get<DevelopmentHistoryFlatCollection>(url);

            return {
                ...result,
                rows: result.rows.map((dto) => this.developmentHistoryMapper.fromDTO(dto)),
            };
        } catch (err) {
            console.log("Error in DevelopmentHistoryServiceImpl.getAllDevelopmentHistory");
            console.log(err);
            throw new Error(String(err));
        }
    }

    async createOrUpdate(dto: IDevelopmentHistory): Promise<IDevelopmentHistory> {
        try {
            const encodedDto = this.developmentHistoryMapper.toDTO(dto);
            if (dto.id) {
                return this.patch(`${this.developmentHistoryEndpoint}/${dto.id}`, encodedDto);
            }

            return this.post(this.developmentHistoryEndpoint, encodedDto);
        } catch (err) {
            console.log("Error in DevelopmentHistoryServiceImpl.createOrUpdateDevelopmentHistory");
            console.log(err);
        }
    }

    async deleteById(id: string): Promise<RemoveResponse> {
        try {
            if (!id) return { status: false };
            return super.delete(`${this.developmentHistoryEndpoint}/${id}`);
        } catch (err) {
            console.log("Error in DevelopmentHistoryServiceImpl.deleteById");
            console.log(err);
        }
    }

    async getById(id: string): Promise<IDevelopmentHistory | null> {
        try {
            if (!id) return null;

            const dto = await this.get<DevelopmentHistoryDTO>(
                `${this.developmentHistoryEndpoint}/${id}?getEntityTranslations=true`
            );
            return this.developmentHistoryMapper.fromDTO(dto);
        } catch (err) {
            console.log("Error in DevelopmentHistoryServiceImpl.getDevelopmentHistoryById");
            console.log(err);
            return null;
        }
    }
}

export function developmentHistoryServiceFactory(): DevelopmentHistoryService {
    return DevelopmentHistoryServiceImpl.getInstance();
}
